import { FC, ReactNode, useRef, useEffect } from "react";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import { Box, Typography } from "@mui/material";
import { green, grey, red } from "@mui/material/colors";
import Highcharts from "highcharts";
import Stack from "@mui/material/Stack";
import { getPercentage } from "@/utils/numeral/getPercentage";
import { ChartErrorBoundary } from "@/storybook/c8r-highcharts/ChartErrorBoundary";

interface GaugeChartProps {
  empty?: boolean;
  error?: boolean;
  partial: number;
  total: number;
  stroke?: boolean;
  fullAngle?: boolean;
  paddingAngle?: number;
  content?: ReactNode;
  innerRadius?: number;
  colors?: { partial: string; total: string };
}

const GaugeChart: FC<GaugeChartProps> = ({
  empty,
  error,
  partial,
  total,
  stroke,
  colors,
  fullAngle,
  paddingAngle,
  content,
  innerRadius,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);

  colors = colors ?? {
    total: green[100],
    partial: red[500],
  };

  if (empty || error) {
    partial = 0;
    total = 0;
  }

  const percentageValue = getPercentage(partial, total);

  useEffect(() => {
    if (!chartRef.current) return;

    const startAngle = fullAngle ? -180 : -135;
    const endAngle = fullAngle ? 180 : 135;

    // @ts-expect-error  | Something wrong with types, this is waiting for "Options" but it should be an "Element"
    const chart = Highcharts.chart(chartRef.current, {
      chart: {
        type: "solidgauge",
        height: 128,
        width: 128,
        backgroundColor: "transparent",
      },

      title: null,

      pane: {
        center: ["50%", "50%"],
        size: "100%",
        startAngle: startAngle,
        endAngle: endAngle,
        background: stroke
          ? [
              {
                backgroundColor: error || empty ? grey[300] : green[500],
                innerRadius: "93%",
                outerRadius: "100%",
                shape: "arc",
                borderWidth: 0,
              },
              {
                backgroundColor: error || empty ? grey[300] : colors.total,
                innerRadius: ((innerRadius ?? 48) / 56) * 100 + "%",
                outerRadius: "93%",
                shape: "arc",
                borderWidth: 0,
              },
            ]
          : [
              {
                backgroundColor: error || empty ? grey[300] : colors.total,
                innerRadius: ((innerRadius ?? 48) / 56) * 100 + "%",
                outerRadius: "93%",
                shape: "arc",
                borderWidth: 0,
              },
            ],
      },

      tooltip: { enabled: false },

      yAxis: {
        stops: [[1, error || empty ? grey[300] : colors.partial]],
        lineWidth: 0,
        tickWidth: 0,
        minorTickInterval: null,
        tickAmount: 0,
        min: 0,
        max: 100,
        labels: { enabled: false },
        accessibility: { enabled: false },
      },

      plotOptions: {
        solidgauge: {
          dataLabels: { enabled: false },
          linecap: "round",
          innerRadius: ((innerRadius ?? 48) / 56) * 100 + "%",
          radius: "93%",
        },
      },

      credits: { enabled: false },

      series:
        percentageValue > 0
          ? [
              {
                name: "Value",
                data: [percentageValue],
                animation: false,
              },
            ]
          : [],
    });

    return () => chart.destroy();
  }, [
    partial,
    total,
    empty,
    error,
    colors,
    stroke,
    fullAngle,
    innerRadius,
    paddingAngle,
    percentageValue,
  ]);

  return (
    <Stack position="relative" justifyContent="center" alignItems="center">
      <Box ref={chartRef} width={128} height={128} />

      <Box
        top="50%"
        left="50%"
        position="absolute"
        sx={{ transform: "translate(-50%,-50%)" }}
      >
        {error ? (
          <ReportProblemRoundedIcon color="inherit" sx={{ color: "#FFCA29" }} />
        ) : (
          <Stack alignItems="center">
            <Typography fontWeight="bold">
              {percentageValue ? percentageValue.toFixed(1) : "0.0"}%
            </Typography>

            {content}
          </Stack>
        )}
      </Box>
    </Stack>
  );
};

export const GaugeChartForAccountCards: FC<GaugeChartProps> = (props) => {
  return (
    <ChartErrorBoundary>
      <GaugeChart {...props} />
    </ChartErrorBoundary>
  );
};
